/**
 * threadOrganizeAuthority — who may arrange their OWN thread list
 * (catalog v13, 2026-09-13; Miles: "all thread management … entirely needs to
 * be allowed or not allowed user by user/role by role according to permission
 * sets").
 *
 * The companion of `threadManageAuthority`, and deliberately a different
 * thing. `threads.manage` governs facts EVERYONE in a thread sees — its name
 * for all of them, who is in it, whether Prest0n is — and the server is the
 * authority on every one of those writes. This key governs the viewer's own
 * copy: the name only they see, and the sections only they have.
 *
 * BE HONEST ABOUT WHAT THIS IS. Personal names and personal sections live on
 * `users/{uid}/chat_threads/{threadId}` and
 * `users/{uid}/chat_threads_meta/categories`, under an owner-only Firestore
 * rule — the viewer owns those documents either way. So this predicate shapes
 * what the UI offers; it is NOT a security boundary, and nothing here should
 * ever be described as one. It exists so an owner can hand someone a role
 * whose sidebar stays as issued, the way `tools.chat-voice.use` exists (R9).
 *
 * Every system role holds the key by template — Read-Only included, because
 * renaming a thread for yourself and filing it in your own section changes
 * nothing anyone else can see (the reasoning of rulings R4 and R9). The only
 * place it can be switched off is a custom role.
 *
 * Fail-open on the same axis as its companion: an RBAC walk still in flight
 * never reads as "no permission" (UNRESOLVED ≠ DENIED, 2026-08-19).
 */
export type ThreadOrganizeAuthorityReason =
  | 'threads-organize'
  | 'owner'
  | 'rbac-unresolved'
  | 'not-permitted';

export interface ThreadOrganizeAuthorityInput {
  isOwner: boolean;
  hasThreadsOrganize: boolean;
  /** False while the viewer's RBAC walk is loading or ended in an error. */
  rbacResolved: boolean;
}

export interface ThreadOrganizeAuthority {
  canOrganize: boolean;
  reason: ThreadOrganizeAuthorityReason;
}

/** The catalog key (every system role by template; off only on a custom role). */
export const THREADS_ORGANIZE_PERMISSION = 'threads.organize-own';

export function resolveThreadOrganizeAuthority(
  input: ThreadOrganizeAuthorityInput,
): ThreadOrganizeAuthority {
  if (input.isOwner) return { canOrganize: true, reason: 'owner' };
  if (input.hasThreadsOrganize) return { canOrganize: true, reason: 'threads-organize' };
  if (!input.rbacResolved) return { canOrganize: true, reason: 'rbac-unresolved' };
  return { canOrganize: false, reason: 'not-permitted' };
}
